import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../features/admin/service";
import type { RecRedisConnectionsResponse } from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import { RecRealtimePanel, RecRedisPanel, RecJobsPanel } from "./AdminRecRedisOpsPanels";
import { num } from "./AdminRealtimeOpsPanels";
import InfoTooltip from "../../components/common/InfoTooltip";
import { useAdminToast } from "../../context/AdminToastContext";

/**
 * Admin ▸ Settings ▸ Rec Redis Health.
 *
 * Sibling of AdminWebSocketHealthPage, scoped entirely to rec_engine's own
 * infrastructure: its dedicated Redis Cloud instance, the WebSocket sockets
 * held by the separate rec-server process, and its worker-lock-gated scheduled
 * jobs. The WebSocket Health tab covers the SHARED backend/RAG/OCR Redis and
 * has no rec-specific data beyond one row in its pool-size table.
 */

const REFRESH_INTERVAL_MS = 5000;

const AdminRecRedisHealthPage: React.FC = () => {
  const [conns, setConns] = useState<RecRedisConnectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  type Tab = "connections" | "realtime" | "redis" | "jobs";
  const [tab, setTab] = useState<Tab>("connections");

  const fetchConnections = useCallback(async () => {
    try {
      const resp = await adminService.getRecRedisConnections();
      setConns(resp);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.detail || "Failed to load rec Redis connection info");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchConnections, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchConnections]);

  const TABS: { id: Tab; label: string; hint: string }[] = [
    { id: "connections", label: "Connections", hint: "Pool sizes and the shared connection ceiling" },
    { id: "realtime", label: "Realtime workers", hint: "Per-worker rec sockets and rec:events fan-out" },
    { id: "redis", label: "Redis", hint: "Memory, connections, namespaces, TTL risk" },
    { id: "jobs", label: "Scheduled jobs", hint: "Are the buyer/seller intel jobs actually running?" },
  ];

  const summary = conns?.summary ?? null;
  const cap = summary?.maxclients_current ?? summary?.maxclients_default ?? null;
  const demand = summary?.estimated_connections ?? null;
  const demandPct = cap && demand ? (demand / cap) * 100 : 0;

  const poolRows = (
    [
      ["rec_engine", conns?.rec_engine ?? null] as const,
      ["rec_api", conns?.rec_api ?? null] as const,
    ] as const
  ).filter(([, info]) => info != null);

  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            title={t.hint}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? "border-b-2 border-indigo-400 text-indigo-300"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "realtime" && <RecRealtimePanel />}
      {tab === "redis" && <RecRedisPanel />}
      {tab === "jobs" && <RecJobsPanel />}

      <div className={tab === "connections" ? "" : "hidden"}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-6">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white">Rec Engine Redis — connections</h2>
            <p className="text-sm text-gray-500 mt-1">
              rec_engine runs on its own dedicated Redis Cloud instance, separate from the
              backend/RAG/OCR one. These are the only two pools that reach it.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:shrink-0 sm:justify-end">
            {lastUpdated && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-indigo-500"
              />
              Auto-refresh
            </label>
            <button
              onClick={fetchConnections}
              className="px-3 py-2 text-sm bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 rounded-lg transition-colors"
            >
              Refresh now
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <p className="flex items-center text-gray-500 text-sm">
              Connection cap
              <InfoTooltip text="The maximum simultaneous clients rec's Redis will accept. Shared by every process below, so a pool size that looks safe on its own can still exhaust it — and a managed plan can enforce a lower limit than this regardless of our CONFIG SET." />
            </p>
            <p className="text-3xl font-bold text-white mt-1">
              {loading ? "—" : num(cap)}
            </p>
            <p className="text-xs text-gray-600 mt-1">REC_REDIS_MAXCLIENTS</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <p className="flex items-center text-gray-500 text-sm">
              Estimated demand
              <InfoTooltip text="Pool size x worker count, added up across BOTH processes that open this instance. This total, not either pool alone, is the number that explains a 'max number of clients reached' error — leave headroom for a deploy overlap and a debug redis-cli session." />
            </p>
            <p
              className={`text-3xl font-bold mt-1 ${
                demandPct > 80 ? "text-amber-400" : "text-white"
              }`}
            >
              {loading ? "—" : num(demand)}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {cap && demand ? `${Math.round(demandPct)}% of cap` : "pool size × workers"}
            </p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <p className="flex items-center text-gray-500 text-sm">
              Rec-server workers
              <InfoTooltip text="How many uvicorn workers the rec-server runs. It comes from docker-compose.rec.yml's --workers, a CLI argument no env var exposes, so this value is kept in step by hand — a naive read from inside the process would report 1 and undercount rec's real demand fourfold." />
            </p>
            <p className="text-3xl font-bold text-white mt-1">
              {summary?.assumed_rec_workers ?? "—"}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              docker-compose.rec.yml --workers
            </p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <p className="flex items-center text-gray-500 text-sm">
              Backend workers
              <InfoTooltip text="How many API workers the backend runs (GUNICORN_WORKERS). Each one opens its own small publish-only pool against this same Redis, which is why the backend contributes to the cap at all." />
            </p>
            <p className="text-3xl font-bold text-white mt-1">
              {summary?.assumed_backend_workers ?? "—"}
            </p>
            <p className="text-xs text-gray-600 mt-1">GUNICORN_WORKERS</p>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-gray-800 space-y-2">
            <p className="text-sm font-medium text-white">How to read this before changing anything</p>
            <p className="text-xs text-gray-500">
              <span className="text-gray-300 font-medium">Two processes, one Redis. </span>
              <span className="text-white font-medium">rec_engine</span> is the dedicated
              rec-server's own pool, opened once per rec worker. <span className="text-white
              font-medium">rec_api</span> is the much smaller pool every backend API worker
              opens purely to fire <code>publish_event()</code> on <code>rec:events</code>.
              Both connect via the same REC_REDIS_URL, so the cap above is shared between
              them — a pool size that looks safe in isolation can still exhaust it.
            </p>
            <p className="text-xs text-gray-500">
              <span className="text-gray-300 font-medium">The math: </span>
              demand = (rec pool × rec workers) + (rec_api pool × backend workers). That
              total, not either pool alone, is what to compare against the cap. Leave
              headroom for a deploy overlap and a debug <code>redis-cli</code> session
              before you hit "max number of clients reached".
            </p>
            <p className="text-xs text-gray-500 pt-1 border-t border-gray-800/60">
              Both pool sizes are edited on the{" "}
              <span className="text-gray-300 font-medium">WebSocket Health</span> tab's pool
              table (they are rows in the same cross-service table); this page is read-only
              so that the rec-specific reading of those numbers lives next to the rest of
              rec's health rather than duplicating the controls.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">
                    <span className="inline-flex items-center">
                      Pool
                      <InfoTooltip text="Which process's connection pool this row describes — the dedicated rec-server's own pool, or the small pool every backend API worker opens purely to publish rec:events." />
                    </span>
                  </th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">
                    <span className="inline-flex items-center">
                      Status
                      <InfoTooltip text="Whether a pool has actually been opened. 'Config only' means the size is known but nothing has connected from the process serving this page — it is not a fault." />
                    </span>
                  </th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">
                    <span className="inline-flex items-center">
                      Default
                      <InfoTooltip text="The env-configured pool size this process starts with, before any admin override." />
                    </span>
                  </th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">
                    <span className="inline-flex items-center">
                      Current live cap
                      <InfoTooltip text="The pool's live max_connections, which an admin override can raise above the default. Measured from THIS process, so the rec_engine row does not reflect the dedicated rec-server's real pool." />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-gray-500">
                      <Spinner />
                    </td>
                  </tr>
                ) : poolRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-gray-500">
                      No rec Redis pool information available
                    </td>
                  </tr>
                ) : (
                  poolRows.map(([key, info]) => (
                    <tr key={key} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4 text-white">{info!.label}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                            info!.connected
                              ? "bg-emerald-500/10 text-emerald-400"
                              : "bg-gray-700/50 text-gray-400"
                          }`}
                        >
                          {info!.connected === null
                            ? "Config only"
                            : info!.connected
                              ? "Connected"
                              : "Not yet connected"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{info!.default}</td>
                      <td className="py-3 px-4 text-gray-400">{info!.current ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {summary?.note && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
            <p className="text-sm font-medium text-white">{summary.label}</p>
            <p className="text-xs text-gray-500 mt-1">{summary.note}</p>
            <p className="text-xs text-gray-500 mt-2">
              The "rec_engine" row above is measured from the BACKEND process, which opens
              the smaller API-side pool — so its live value does not reflect the dedicated
              rec-server's real pool. That is why the demand estimate adds both
              contributions explicitly instead of reading one row.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRecRedisHealthPage;
